import React, { FC } from "react";
import { Routes, Route } from "react-router";
import { observer } from "mobx-react";

import Claims from "../pages/Claims";
import Dashboard from "../pages/Dashboard";
import Home from "../pages/Home";
import HomePage from "./ui/home/HomePage";
import SearchResults from "../pages/SearchResults";
import { ContractResearchPage } from "../pages/ContractResearchPage";
import { PolicyQuestionsPage } from "../pages/PolicyQuestionsPage";
import ChatPage from "../pages/ChatPage";
import PreServiceReview from "./preChats/PreServiceReview";

import Header from "./Header";
import Sidebar from "./Sidebar";
import Column from "./ui/core/Column";
import Row from "./ui/core/Row";
import AppealAClaim from "./preChats/AppealAClaim";
import AppealReview from "./preChats/AppealReview";
import DocumentsReview from "./preChats/DocumentsReview";
import { ReleaseNotes } from "../pages/ReleaseNotes";
import TicketsPage from "../pages/TicketsPage";
import TicketDetailPage from "../pages/TicketDetailPage";
import { useAuth } from "../contexts/AuthContext";
import NotFoundPage from "./NotFoundPage";
import TicketingRoute from "./TicketingRoute";
import { useCapabilities } from "../contexts/permissions/useCapabilities";
import { useFCCapabilities } from "../contexts/permissions/useFCCapabilities";

// Layout component that uses SidebarContext (must be inside SidebarProvider)
const Layout: FC = observer(() => {
  const { is_external } = useAuth();
  const { showSidebar, showHome, showDashboard, showClaims } =
    useCapabilities();
  const { header } = useFCCapabilities();

  if (is_external) {
    return (
      <Row>
        {showSidebar ? <Sidebar /> : null}

        <Column>
          <Header />

          <main className="flex-1 flex flex-col overflow-hidden min-h-0">
            <Routes>
              {showHome ? (
                <>
                  <Route path="/" element={<Home />} />
                  
                  <Route path="/new-chat" element={<HomePage isNewChat />} />

                  <Route
                    path="/contract-research"
                    element={<ContractResearchPage />}
                  />
                  <Route
                    path="/policy-questions"
                    element={<PolicyQuestionsPage />}
                  />
                  <Route path="/appeal-claim" element={<AppealAClaim />} />
                  <Route path="/appeal-review" element={<AppealReview />} />
                  <Route
                    path="/documents-review"
                    element={<DocumentsReview />}
                  />
                  <Route
                    path="/pre-service-review"
                    element={<PreServiceReview />}
                  />
                </>
              ) : null}
              {showDashboard ? (
                <Route path="/dashboard" element={<Dashboard />} />
              ) : null}
              {showClaims ? (
                <Route path="/claims" element={<Claims />} />
              ) : null}
              <Route
                path="/tickets"
                element={
                  <TicketingRoute>
                    <TicketsPage />
                  </TicketingRoute>
                }
              />
              <Route
                path="/tickets/:ticketId"
                element={
                  <TicketingRoute>
                    <TicketDetailPage />
                  </TicketingRoute>
                }
              />
              <Route path="/channels/:channelId" element={<ChatPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </main>
        </Column>
      </Row>
    );
  }

  return (
    <Row>
      <Sidebar />

      <Column>
        <Header />

        <main className="flex-1 flex flex-col overflow-hidden min-h-0">
          <Routes>
            {header?.home !== false ? (
              <>
                <Route path="/" element={<Home />} />

                <Route path="/new-chat" element={<HomePage isNewChat />} />
              </>
            ) : null}

            {header?.claims !== false ? (
              <Route path="/claims" element={<Claims />} />
            ) : null}
            {header?.dashboard !== false ? (
              <Route path="/dashboard" element={<Dashboard />} />
            ) : null}
            <Route path="/search/:query" element={<SearchResults />} />
            <Route path="/channels/:channelId" element={<ChatPage />} />
            <Route
              path="/contract-research"
              element={<ContractResearchPage />}
            />
            <Route path="/policy-questions" element={<PolicyQuestionsPage />} />
            <Route path="/appeal-claim" element={<AppealAClaim />} />
            <Route path="/appeal-review" element={<AppealReview />} />
            <Route path="/documents-review" element={<DocumentsReview />} />
            <Route path="/pre-service-review" element={<PreServiceReview />} />
            <Route path="/release-notes" element={<ReleaseNotes />} />
            <Route
              path="/tickets"
              element={
                <TicketingRoute>
                  <TicketsPage />
                </TicketingRoute>
              }
            />
            <Route
              path="/tickets/:ticketId"
              element={
                <TicketingRoute>
                  <TicketDetailPage />
                </TicketingRoute>
              }
            />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </main>
      </Column>
    </Row>
  );
});

export default Layout;
